import React, { useEffect, useMemo, useState } from "react";
import AppButton from "../../components/AppButton";
import AppWorkspace from "../../components/AppWorkspace";
import { AppToastProvider } from "../../components/AppToastProvider";
import { IconChevronDown } from "../../icons";
import { designSystemDocGroups } from "./docRegistry";
import "../../styles/tokens.css";
import "../../styles/components.css";
import "./designSystemCatalog.css";

const initialExpandedNavGroups = Object.fromEntries(designSystemDocGroups.map(({ key }) => [key, true]));
const groupAnchorIds = Object.fromEntries(designSystemDocGroups.map(({ key }) => [key, `ds-group-${key}`]));

export default function DesignSystemPage() {
  const [expandedNavGroups, setExpandedNavGroups] = useState(initialExpandedNavGroups);
  const [filterValue, setFilterValue] = useState("");
  const [activeDocKey, setActiveDocKey] = useState(designSystemDocGroups[0]?.docs[0]?.key ?? null);

  function setNavGroupExpanded(groupKey, nextExpanded) {
    setExpandedNavGroups((current) => ({
      ...current,
      [groupKey]: nextExpanded,
    }));
  }

  function scrollToSection(sectionId) {
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  function renderToggleButton(expanded, onClick, label) {
    return (
      <AppButton
        type="button"
        variant="muted"
        size="sm"
        iconOnly
        className={`ds-docs-section-card__toggle${expanded ? " is-expanded" : ""}`}
        aria-expanded={expanded}
        aria-label={label}
        onClick={onClick}
      >
        <IconChevronDown sx={{ fontSize: 18 }} />
      </AppButton>
    );
  }

  const normalizedFilter = filterValue.trim().toLocaleLowerCase("de");
  const filteredDocGroups = useMemo(
    () =>
      designSystemDocGroups
        .map((group) => ({
          ...group,
          docs: group.docs.filter((doc) => doc.title.toLocaleLowerCase("de").includes(normalizedFilter)),
        }))
        .filter((group) => group.docs.length > 0),
    [normalizedFilter]
  );
  const visibleDocs = useMemo(() => filteredDocGroups.flatMap(({ docs }) => docs), [filteredDocGroups]);

  useEffect(() => {
    if (!visibleDocs.length) {
      setActiveDocKey(null);
      return undefined;
    }

    setActiveDocKey((currentKey) =>
      visibleDocs.some((doc) => doc.key === currentKey) ? currentKey : visibleDocs[0].key
    );

    const contentScrollRoot = document.querySelector(".design-system-page__scroll-area");
    const mainScrollRoot = document.querySelector(".design-system-page__main");
    const scrollRoots = [contentScrollRoot, mainScrollRoot].filter(Boolean);
    if (!scrollRoots.length) {
      return undefined;
    }

    let animationFrame = null;

    function updateActiveDoc() {
      animationFrame = null;
      const scrollRoot = mainScrollRoot && mainScrollRoot.scrollHeight > mainScrollRoot.clientHeight
        ? mainScrollRoot
        : contentScrollRoot;
      if (!scrollRoot) return;
      const rootTop = scrollRoot.getBoundingClientRect().top;
      const activationOffset = 24;
      let nextActiveDoc = visibleDocs[0];

      for (const doc of visibleDocs) {
        const element = document.getElementById(doc.anchorId);
        if (!element) {
          continue;
        }

        const top = element.getBoundingClientRect().top - rootTop;
        if (top <= activationOffset) {
          nextActiveDoc = doc;
        } else {
          break;
        }
      }

      setActiveDocKey((currentKey) => (currentKey === nextActiveDoc.key ? currentKey : nextActiveDoc.key));
    }

    function requestActiveDocUpdate() {
      if (animationFrame === null) {
        animationFrame = window.requestAnimationFrame(updateActiveDoc);
      }
    }

    requestActiveDocUpdate();
    scrollRoots.forEach((scrollRoot) => {
      scrollRoot.addEventListener("scroll", requestActiveDocUpdate, { passive: true });
    });
    window.addEventListener("resize", requestActiveDocUpdate);

    return () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }
      scrollRoots.forEach((scrollRoot) => {
        scrollRoot.removeEventListener("scroll", requestActiveDocUpdate);
      });
      window.removeEventListener("resize", requestActiveDocUpdate);
    };
  }, [visibleDocs]);

  return (
    <AppToastProvider>
      <AppWorkspace
        className="design-system-page__workspace"
        title="Design System"
        contentClassName="design-system-page__scroll-area"
      >
        <div className="ds-page design-system-page">
          <div className="design-system-page__layout">
            <aside className="design-system-page__sidebar">
              <div className="design-system-page__sidebar-inner">
                <input
                  type="text"
                  value={filterValue}
                  onChange={(event) => setFilterValue(event.target.value)}
                  placeholder="Kapitel filtern"
                  className="design-system-page__filter"
                />

                <div className="design-system-page__nav-stack">
                  {filteredDocGroups.map((group) => {
                    const isNavGroupExpanded = normalizedFilter ? true : expandedNavGroups[group.key];

                    return (
                      <section key={group.key} className="design-system-page__nav-group">
                        <div className="design-system-page__nav-group-header">
                          <button
                            type="button"
                            className="design-system-page__nav-group-button"
                            onClick={() => scrollToSection(groupAnchorIds[group.key])}
                          >
                            <span className="design-system-page__nav-group-title">{group.title}</span>
                            <span className="design-system-page__nav-group-count">{group.docs.length}</span>
                          </button>
                          {renderToggleButton(
                            isNavGroupExpanded,
                            () => setNavGroupExpanded(group.key, !expandedNavGroups[group.key]),
                            isNavGroupExpanded ? `${group.title} einklappen` : `${group.title} ausklappen`
                          )}
                        </div>

                        {isNavGroupExpanded ? (
                          <div className="design-system-page__nav-links">
                            {group.docs.map((doc) => (
                              <button
                                key={doc.key}
                                type="button"
                                className={`design-system-page__nav-link${
                                  activeDocKey === doc.key ? " is-active" : ""
                                }`}
                                aria-current={activeDocKey === doc.key ? "true" : undefined}
                                onClick={() => scrollToSection(doc.anchorId)}
                              >
                                {doc.title}
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </section>
                    );
                  })}
                </div>
              </div>
            </aside>

            <main className="design-system-page__main">
              {filteredDocGroups.length === 0 ? (
                <section className="design-system-page__empty-state">
                  <h2 className="design-system-page__group-title">Keine Treffer</h2>
                </section>
              ) : null}

              {filteredDocGroups.map((group) => (
                <section
                  key={group.key}
                  id={groupAnchorIds[group.key]}
                  className="design-system-page__group design-system-page__anchor-target"
                >
                  <div className="design-system-page__group-header">
                    <h2 className="design-system-page__group-title">{group.title}</h2>
                  </div>

                  <div className="design-system-page__group-stack">
                    {group.docs.map(({ key, anchorId, Component }) => (
                      <section key={key} id={anchorId} className="design-system-page__anchor-target">
                        <Component />
                      </section>
                    ))}
                  </div>
                </section>
              ))}
            </main>
          </div>
        </div>
      </AppWorkspace>
    </AppToastProvider>
  );
}
